import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import AgentPanel, { OPEN_AGENT_EVENT } from '../../../pages/ai-explorer/agent/AgentPanel';
import './ChatBubble.css';

const BUBBLE_POSITION_KEY = 'saf3ai_bubble_position';
const DRAG_THRESHOLD = 5; // px – distance before a press counts as drag
const BUBBLE_SIZE = 58;

/**
 * The floating draggable launcher for the Saf3AI Agent.
 *
 * This component owns the button and nothing else. Clicking it opens
 * AgentPanel — the Saf3AI Agent as a resizable panel over the page —
 * which is the assistant experience the product is specified around. The
 * bubble deliberately does not implement a chat surface of its own: a second
 * implementation drifts from the panel on context handling, agent modes and
 * recommendations, which is exactly what happened the last time one existed.
 */
const ChatBubble = () => {
  const [isOpen, setIsOpen] = useState(false);

  // ── Drag state ──
  const [bubblePos, setBubblePos] = useState(() => {
    try {
      const saved = localStorage.getItem(BUBBLE_POSITION_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Re-clamp to the current viewport — saved coords may be from a wider
        // window, which would land the bubble off-screen on a narrower one.
        const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
        const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
        return {
          right: Math.max(4, Math.min(parsed.right ?? 28, vw - BUBBLE_SIZE - 4)),
          bottom: Math.max(4, Math.min(parsed.bottom ?? 28, vh - BUBBLE_SIZE - 4)),
        };
      }
    } catch { /* ignore */ }
    return { right: 28, bottom: 28 };
  });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    startRight: 0,
    startBottom: 0,
    didDrag: false,
  });

  // The header shortcut and the floating button must open the same assistant.
  // Keep this event-based so no other surface needs to own a second panel.
  // `openAgent({ question, subject })` from any page lands here with the
  // question pre-sent; ⌘J toggles the panel.
  const [launch, setLaunch] = useState({ question: '', subject: null });
  useEffect(() => {
    const openFromEvent = (e) => {
      const d = e?.detail || {};
      setLaunch({ question: d.question || '', subject: d.subject || null, draft: Boolean(d.draft), at: Date.now() });
      setIsOpen(true);
    };
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'j') { e.preventDefault(); setIsOpen((o) => !o); }
    };
    window.addEventListener(OPEN_AGENT_EVENT, openFromEvent);
    document.addEventListener('keydown', onKey);
    return () => { window.removeEventListener(OPEN_AGENT_EVENT, openFromEvent); document.removeEventListener('keydown', onKey); };
  }, []);

  // Feather renders the button glyph after mount and whenever it swaps.
  useEffect(() => {
    if (typeof window !== 'undefined' && window.feather) {
      setTimeout(() => window.feather.replace(), 100);
    }
  }, [isOpen]);

  // ── Drag handlers ──────────────────────────────────────────────────
  const handleDragStart = useCallback((clientX, clientY) => {
    dragRef.current = {
      active: true,
      startX: clientX,
      startY: clientY,
      startRight: bubblePos.right,
      startBottom: bubblePos.bottom,
      didDrag: false,
    };
  }, [bubblePos]);

  const handleDragMove = useCallback((clientX, clientY) => {
    const d = dragRef.current;
    if (!d.active) return;

    const dx = clientX - d.startX;
    const dy = clientY - d.startY;
    if (!d.didDrag && Math.sqrt(dx * dx + dy * dy) < DRAG_THRESHOLD) return;
    d.didDrag = true;
    setIsDragging(true);

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let newRight = d.startRight - dx;
    let newBottom = d.startBottom - dy;

    // Clamp to viewport
    newRight = Math.max(4, Math.min(newRight, vw - BUBBLE_SIZE - 4));
    newBottom = Math.max(4, Math.min(newBottom, vh - BUBBLE_SIZE - 4));

    setBubblePos({ right: newRight, bottom: newBottom });
  }, []);

  const handleDragEnd = useCallback(() => {
    const didDrag = dragRef.current.didDrag;
    dragRef.current.active = false;
    setIsDragging(false);

    if (didDrag) {
      // Persist position
      try { localStorage.setItem(BUBBLE_POSITION_KEY, JSON.stringify(bubblePos)); } catch { /* ignore */ }
    } else {
      // It was a click, not a drag — open the large Axon panel.
      setIsOpen((prev) => !prev);
    }
  }, [bubblePos]);

  // Re-clamp the bubble whenever the viewport shrinks so it never lands off-screen.
  useEffect(() => {
    const reclamp = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      setBubblePos((p) => {
        const next = {
          right: Math.max(4, Math.min(p.right, vw - BUBBLE_SIZE - 4)),
          bottom: Math.max(4, Math.min(p.bottom, vh - BUBBLE_SIZE - 4)),
        };
        if (next.right === p.right && next.bottom === p.bottom) return p;
        return next;
      });
    };
    window.addEventListener('resize', reclamp);
    return () => window.removeEventListener('resize', reclamp);
  }, []);

  // Mouse events
  const onMouseDown = useCallback((e) => {
    if (e.button !== 0) return; // left-click only
    e.preventDefault();
    handleDragStart(e.clientX, e.clientY);
  }, [handleDragStart]);

  useEffect(() => {
    const onMouseMove = (e) => handleDragMove(e.clientX, e.clientY);
    const onMouseUp = () => { if (dragRef.current.active) handleDragEnd(); };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [handleDragMove, handleDragEnd]);

  // Touch events
  const onTouchStart = useCallback((e) => {
    const t = e.touches[0];
    handleDragStart(t.clientX, t.clientY);
  }, [handleDragStart]);

  useEffect(() => {
    const onTouchMove = (e) => {
      const t = e.touches[0];
      handleDragMove(t.clientX, t.clientY);
    };
    const onTouchEnd = () => { if (dragRef.current.active) handleDragEnd(); };

    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
    return () => {
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [handleDragMove, handleDragEnd]);

  // ─── Render ────────────────────────────────────────────────────────────
  const bubbleContent = (
    <button
      className={`chat-bubble-btn${isOpen ? ' open' : ''}${isDragging ? ' dragging' : ''}`}
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
      title={isOpen ? 'Close Saf3AI Agent' : 'Chat with AI Agent'}
      id="chat-bubble-trigger"
      style={{
        right: bubblePos.right,
        bottom: bubblePos.bottom,
        transition: isDragging ? 'none' : undefined,
      }}
    >
      <i data-feather={isOpen ? 'x' : 'message-circle'}></i>
    </button>
  );

  return (
    <>
      {createPortal(bubbleContent, document.body)}
      <AgentPanel
        isOpen={isOpen}
        onClose={() => { setIsOpen(false); setLaunch({ question: '', subject: null }); }}
        initialMessage={launch.question}
        initialSubject={launch.subject}
        initialDraft={launch.draft}
        launchKey={launch.at}
      />
    </>
  );
};

export default ChatBubble;
